import React, { useState, useEffect, useCallback } from 'react';
import {
  Server, Database, Zap, Package, Archive, Cloud,
  ShieldAlert, ShieldCheck, Tag, TrendingDown,
  ChevronDown, ChevronUp, RefreshCw,
  AlertTriangle, Info, CheckCircle, XCircle,
  Activity, HardDrive, Globe, Camera, Clock, List, LayoutGrid, Trash2,
  ScrollText, ShieldOff, KeyRound, Network, UserCog, LogIn, FileText, AlertOctagon,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const TABS = [
  { id: 'inventory',    label: 'Inventario',    icon: Server },
  { id: 'security',     label: 'Seguridad',      icon: ShieldAlert },
  { id: 'tags',         label: 'Tags',            icon: Tag },
  { id: 'optimization', label: 'Optimización',    icon: TrendingDown },
  { id: 'audit',        label: 'Auditoría',       icon: ScrollText },
  { id: 'logs',         label: 'Logs',            icon: FileText },
];

const AUDIT_CATS = [
  { id: 'root',           label: 'Uso de root',    icon: KeyRound,  color: 'var(--red)' },
  { id: 'login_fallido',  label: 'Logins fallidos', icon: LogIn,     color: 'var(--amber)' },
  { id: 'login_sin_mfa',  label: 'Login sin MFA',   icon: LogIn,     color: 'var(--amber)' },
  { id: 'destructivo',    label: 'Destructivos',    icon: Trash2,    color: 'var(--red)' },
  { id: 'iam',            label: 'Cambios IAM',     icon: UserCog,   color: 'var(--amber)' },
  { id: 'red',            label: 'Red / SG',        icon: Network,   color: 'var(--amber)' },
  { id: 'anti_auditoria', label: 'Anti-auditoría',  icon: ShieldOff, color: 'var(--red)' },
];

const SEVERITY_STYLE = {
  critical: { bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)', label: 'Crítico',  Icon: XCircle },
  medium:   { bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)', label: 'Medio',    Icon: AlertTriangle },
  info:     { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)', label: 'Info',     Icon: Info },
};

const SERVICE_ICON = { EC2: Server, RDS: Database, Lambda: Zap, ECS: Package, S3: Archive };

function pill(text, bg = 'var(--border)', color = 'var(--ink-2)', border = 'var(--border)') {
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

function stateBadge(state) {
  const map = {
    running:   ['#dcfce7', 'var(--green)', '#bbf7d0'],
    available: ['#dcfce7', 'var(--green)', '#bbf7d0'],
    active:    ['#dcfce7', 'var(--green)', '#bbf7d0'],
    stopped:   ['#f1f5f9', 'var(--ink-2)', '#e2e8f0'],
    stopping:  ['#fff7ed', '#92400e', '#fed7aa'],
    terminated:['#fef2f2', 'var(--red)', '#fecaca'],
  };
  const [bg, color, border] = map[state?.toLowerCase()] || ['#f1f5f9', 'var(--ink-2)', '#e2e8f0'];
  return pill(state || '—', bg, color, border);
}

// ── Section collapse wrapper ──────────────────────────────────────────────────
function Section({ title, icon: Icon, count, color = 'var(--accent)', children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', background: 'var(--bg-subtle)', border: 'none', cursor: 'pointer',
        textAlign: 'left',
      }}>
        <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--amber-dim)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} style={{ color }} />
        </div>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', flex: 1 }}>{title}</span>
        {count != null && count !== '' && (
          <span style={{ fontSize: 12, color: 'var(--ink-2)', marginRight: 8 }}>
            {typeof count === 'number' ? `${count} recursos` : count}
          </span>
        )}
        {open ? <ChevronUp size={16} color="var(--ink-3)" /> : <ChevronDown size={16} color="var(--ink-3)" />}
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

// ── Generic table ─────────────────────────────────────────────────────────────
function Table({ cols, rows, emptyMsg = 'Sin datos' }) {
  if (!rows.length) return <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 12 }}>{emptyMsg}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
            {cols.map(c => (
              <th key={c.key} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--bg)' }}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '9px 12px', color: 'var(--ink-2)', whiteSpace: c.wrap ? 'normal' : 'nowrap' }}>
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: Inventario ───────────────────────────────────────────────────────────
function TabInventory({ data, loading, error }) {
  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return <EmptySelect />;

  const { ec2, rds, lambda, ecs, s3, errors } = data;

  return (
    <div>
      {Object.values(errors || {}).filter(Boolean).map((e, i) => (
        <div key={i} style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 6,
          padding: '8px 12px', marginBottom: 8, fontSize: 12, color: 'var(--red)' }}>{e}</div>
      ))}

      <Section title="EC2 Instances" icon={Server} count={ec2.length}>
        <Table cols={[
          { key: 'name',      label: 'Nombre' },
          { key: 'id',        label: 'ID' },
          { key: 'type',      label: 'Tipo' },
          { key: 'state',     label: 'Estado',    render: v => stateBadge(v) },
          { key: 'az',        label: 'AZ' },
          { key: 'privateIp', label: 'IP Privada' },
          { key: 'publicIp',  label: 'IP Pública', render: v => v || <span style={{ color: 'var(--ink-3)' }}>—</span> },
          { key: 'platform',  label: 'SO',         render: v => pill(v) },
        ]} rows={ec2} />
      </Section>

      <Section title="RDS Instances" icon={Database} count={rds.length} color="var(--ink-2)">
        <Table cols={[
          { key: 'name',     label: 'Identificador' },
          { key: 'engine',   label: 'Engine' },
          { key: 'class',    label: 'Clase' },
          { key: 'status',   label: 'Estado',   render: v => stateBadge(v) },
          { key: 'storage',  label: 'Storage',  render: v => `${v} GB` },
          { key: 'multiAz',  label: 'Multi-AZ', render: v => v
            ? <CheckCircle size={14} color="var(--green)" />
            : <XCircle size={14} color="var(--red)" /> },
          { key: 'encrypted', label: 'Encriptado', render: v => v
            ? <CheckCircle size={14} color="var(--green)" />
            : <XCircle size={14} color="var(--red)" /> },
        ]} rows={rds} />
      </Section>

      <Section title="Lambda Functions" icon={Zap} count={lambda.length} color="var(--amber)">
        <Table cols={[
          { key: 'name',         label: 'Función' },
          { key: 'runtime',      label: 'Runtime',  render: v => pill(v) },
          { key: 'memory',       label: 'Memoria',  render: v => `${v} MB` },
          { key: 'timeout',      label: 'Timeout',  render: v => `${v}s` },
          { key: 'codeSize',     label: 'Código',   render: v => `${(v / 1024).toFixed(0)} KB` },
          { key: 'lastModified', label: 'Modificada', render: v => v ? new Date(v).toLocaleDateString() : '—' },
        ]} rows={lambda} />
      </Section>

      <Section title="ECS Clusters" icon={Package} count={ecs.length} color="var(--ink-2)">
        {ecs.length === 0
          ? <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Sin clusters ECS</div>
          : ecs.map(cluster => (
            <div key={cluster.id} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{cluster.name}</span>
                {stateBadge(cluster.status)}
                <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  {cluster.runningTasksCount} tasks corriendo · {cluster.activeServicesCount} servicios
                </span>
              </div>
              <Table cols={[
                { key: 'name',         label: 'Servicio' },
                { key: 'status',       label: 'Estado',   render: v => stateBadge(v) },
                { key: 'runningCount', label: 'Running',  render: (v, r) => `${v}/${r.desiredCount}` },
                { key: 'launchType',   label: 'Launch',   render: v => pill(v) },
              ]} rows={cluster.services} />
            </div>
          ))
        }
      </Section>

      <Section title="S3 Buckets" icon={Archive} count={s3.length} color="var(--green)">
        <Table cols={[
          { key: 'name',               label: 'Bucket' },
          { key: 'creationDate',       label: 'Creado', render: v => v ? new Date(v).toLocaleDateString() : '—' },
          { key: 'publicAccessBlocked', label: 'Block Public Access', render: v => v
            ? <CheckCircle size={14} color="var(--green)" />
            : <XCircle size={14} color="var(--red)" /> },
          { key: 'isPublic',           label: 'Público', render: v => v
            ? pill('PÚBLICO', '#fef2f2', 'var(--red)', '#fecaca')
            : pill('Privado', '#f0fdf4', 'var(--green)', '#bbf7d0') },
        ]} rows={s3} />
      </Section>
    </div>
  );
}

// ── Tab: Seguridad ────────────────────────────────────────────────────────────
// Fila de rol expandible: muestra dónde se usa al hacer click
function RoleRow({ r }) {
  const [open, setOpen] = useState(false);
  const has = r.usedBy.length > 0;
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div onClick={() => has && setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: has ? 'pointer' : 'default' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
        {r.adminLike && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--amber)', flexShrink: 0 }}>admin / *:*</span>}
        <span style={{ fontSize: 11, color: has ? 'var(--accent)' : 'var(--ink-3)', fontWeight: has ? 600 : 400, flexShrink: 0 }}>
          {has ? `${r.usedBy.length} recurso(s)` : 'sin uso'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {r.lastUsed ? new Date(r.lastUsed).toLocaleDateString('es-AR') : '—'}
        </span>
        {has ? (open ? <ChevronUp size={14} color="var(--ink-3)" /> : <ChevronDown size={14} color="var(--ink-3)" />) : <span style={{ width: 14 }} />}
      </div>
      {open && has && (
        <div style={{ padding: '2px 10px 10px 10px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {r.usedBy.map((u, j) => {
            const [svc, ...rest] = u.split(':');
            return <span key={j} style={{ fontSize: 11, background: 'var(--bg-subtle)', color: 'var(--ink-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 7px' }}>
              <strong style={{ color: 'var(--accent)' }}>{svc}</strong> {rest.join(':')}
            </span>;
          })}
        </div>
      )}
    </div>
  );
}

// Tarjeta de un hallazgo de seguridad
function FindingCard({ f }) {
  const s = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.info;
  const SvcIcon = SERVICE_ICON[f.service] || Cloud;
  return (
    <div style={{ border: `1px solid ${s.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 8,
      background: s.bg, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <s.Icon size={18} style={{ color: s.color, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
          <SvcIcon size={12} style={{ color: s.color }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: s.color, textTransform: 'uppercase' }}>{f.service}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{f.issue}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{f.resource}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{f.detail}</div>
        {Array.isArray(f.affectedResources) && f.affectedResources.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 3 }}>Equipos afectados ({f.affectedResources.length}):</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {f.affectedResources.map((r, j) => (
                <span key={j} style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink-2)',}}>
                  <strong style={{ color: s.color }}>{r.type}</strong> {r.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabSecurity({ data, loading, error }) {
  const [showScore, setShowScore] = useState(false);
  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return <EmptySelect />;

  const { findings = [], critical, medium, info, score, grade, scoreBreakdown = [], iam } = data;
  const GRADE_COLOR = { A: 'var(--green)', B: 'var(--amber)', C: 'var(--amber)', D: 'var(--amber)', F: 'var(--red)' };
  const gColor = GRADE_COLOR[grade] || 'var(--ink-2)';
  const bySev = {
    critical: findings.filter(f => f.severity === 'critical'),
    medium:   findings.filter(f => f.severity === 'medium'),
    info:     findings.filter(f => f.severity === 'info'),
  };

  return (
    <div>
      {/* Security score + severidades */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {score != null && (
          <div onClick={() => setShowScore(v => !v)} title="Ver cómo se calcula"
            style={{ flex: '0 0 auto', minWidth: 150, border: `1px solid ${gColor}55`, borderRadius: 8,
            padding: '12px 20px', background: `${gColor}0f`, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
            <div style={{ fontSize: 40, fontWeight: 800, color: gColor, lineHeight: 1 }}>{grade}</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)' }}>{score}<span style={{ fontSize: 13, color: 'var(--ink-3)' }}>/100</span></div>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600 }}>Security Score</div>
              <div style={{ fontSize: 10, color: gColor, fontWeight: 600, marginTop: 2 }}>{showScore ? '▲ ocultar' : 'ⓘ cómo se calcula'}</div>
            </div>
          </div>
        )}
        {[
          { label: 'Críticos', count: critical, ...SEVERITY_STYLE.critical },
          { label: 'Medios',   count: medium,   ...SEVERITY_STYLE.medium },
          { label: 'Info',     count: info,      ...SEVERITY_STYLE.info },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, minWidth: 110, border: `1px solid ${s.border}`, borderRadius: 8,
            padding: '14px 16px', background: s.bg }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Explicación del score */}
      {showScore && score != null && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, background: 'var(--bg-subtle)' }}>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8 }}>
            Arranca en <strong>100</strong> y resta por cada problema. Grade: A ≥90 · B ≥75 · C ≥60 · D ≥40 · F &lt;40.
          </div>
          {scoreBreakdown.length === 0
            ? <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>Sin penalizaciones — postura óptima.</div>
            : <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: 'var(--ink-2)' }}><span>Base</span><span>100</span></div>
                {scoreBreakdown.map((b, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderTop: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--ink-2)' }}>{b.label}</span>
                    <span style={{ color: 'var(--red)', fontWeight: 600 }}>{b.points}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0 0', borderTop: '2px solid var(--border)', fontWeight: 700, marginTop: 4 }}>
                  <span>Score final</span><span style={{ color: gColor }}>{score}/100</span>
                </div>
              </>}
        </div>
      )}

      {/* IAM — postura de cuenta */}
      {iam?.available && (
        <Section title="IAM — Postura de cuenta" icon={ShieldAlert} count={null} color="var(--red)" defaultOpen>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { ok: iam.account.rootMfa === true, label: 'Root con MFA' },
              { ok: iam.account.rootKeys === false, label: 'Root sin access keys' },
              { ok: iam.account.passwordPolicy === true, label: 'Password policy' },
              { ok: (iam.account.usersNoMfa || 0) === 0, label: `Usuarios sin MFA: ${iam.account.usersNoMfa ?? '—'}` },
              { ok: (iam.account.oldKeys || 0) === 0, label: `Keys >90d: ${iam.account.oldKeys ?? '—'}` },
            ].map((c, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                padding: '5px 12px', borderRadius: 20,
                background: c.ok ? 'var(--green-dim)' : 'var(--red-dim)', color: c.ok ? 'var(--green)' : 'var(--red)',
                border: `1px solid ${c.ok ? 'var(--green-dim)' : 'var(--red-dim)'}` }}>
                {c.ok ? <CheckCircle size={13} /> : <XCircle size={13} />} {c.label}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* IAM — roles y dónde se usan */}
      {iam?.available && (iam.roles || []).length > 0 && (
        <Section title="Roles y dónde se usan" icon={KeyRound}
          count={`${iam.roles.length}${iam.totalRoles ? ' de ' + iam.totalRoles : ''} roles`} color="var(--ink-2)" defaultOpen={false}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 8 }}>Click en un rol para ver dónde se usa (EC2 / Lambda). Ordenados por privilegio y uso.</div>
          {iam.roles.map((r, i) => <RoleRow key={i} r={r} />)}
        </Section>
      )}

      {/* Hallazgos agrupados por severidad, colapsables */}
      {findings.length === 0
        ? <div style={{ textAlign: 'center', padding: 48, color: 'var(--green)' }}>
            <ShieldCheck size={40} style={{ marginBottom: 8 }} />
            <div style={{ fontWeight: 600 }}>Sin hallazgos de seguridad</div>
          </div>
        : ['critical', 'medium', 'info'].map(sev => bySev[sev].length > 0 && (
            <Section key={sev}
              title={sev === 'critical' ? 'Hallazgos críticos' : sev === 'medium' ? 'Hallazgos medios' : 'Hallazgos informativos'}
              icon={SEVERITY_STYLE[sev].Icon} color={SEVERITY_STYLE[sev].color}
              count={String(bySev[sev].length)} defaultOpen={sev === 'critical'}>
              {bySev[sev].map((f, i) => <FindingCard key={i} f={f} />)}
            </Section>
          ))
      }
    </div>
  );
}

// ── Tab: Tags ─────────────────────────────────────────────────────────────────
function TabTags({ data, loading, error }) {
  const [filter, setFilter] = useState('all');
  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return <EmptySelect />;

  const { resources, requiredTags, total } = data;
  const services = ['all', ...new Set(resources.map(r => r.service))];
  const filtered = filter === 'all' ? resources : resources.filter(r => r.service === filter);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          Tags requeridos: {requiredTags.map(t => (
            <span key={t} style={{ marginLeft: 4 }}>{pill(t, '#fff7ed', '#1668D6', '#fed7aa')}</span>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {services.map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: '4px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              border: '1px solid var(--border-2)', fontWeight: filter === s ? 600 : 400,
              background: filter === s ? 'var(--ink)' : 'var(--surface)',
              color: filter === s ? 'var(--surface)' : 'var(--ink-2)',
            }}>
              {s === 'all' ? `Todos (${total})` : s}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0
        ? <div style={{ textAlign: 'center', padding: 48, color: 'var(--green)' }}>
            <CheckCircle size={40} style={{ marginBottom: 8 }} />
            <div style={{ fontWeight: 600 }}>Todos los recursos tienen los tags requeridos</div>
          </div>
        : <Table cols={[
            { key: 'service', label: 'Servicio', render: v => {
              const Icon = SERVICE_ICON[v] || Cloud;
              return <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={13} />{v}</div>;
            }},
            { key: 'name',        label: 'Recurso' },
            { key: 'missingTags', label: 'Tags faltantes', render: v => (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {v.map(t => pill(t, '#fef2f2', 'var(--red)', '#fecaca'))}
              </div>
            )},
            { key: 'existingTags', label: 'Tags existentes', wrap: true, render: v => {
              const keys = Object.keys(v).slice(0, 5);
              return (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {keys.map(k => pill(`${k}: ${v[k]}`))}
                  {Object.keys(v).length > 5 && pill(`+${Object.keys(v).length - 5} más`)}
                </div>
              );
            }},
          ]} rows={filtered} />
      }
    </div>
  );
}

// ── Tab: Auditoría (CloudTrail) ───────────────────────────────────────────────
function relTime(ts) {
  const d = new Date(ts);
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function TabAudit({ accountId, token }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback((h) => {
    if (!accountId || !token) return;
    setLoading(true); setError(null);
    fetch(`${API}/api/aws/inventory/audit?accountId=${accountId}&hours=${h}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Error'); return d; })
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountId, token]);

  useEffect(() => { load(hours); }, [load, hours]);

  if (!accountId) return <EmptySelect />;

  const events = data?.events || [];
  const filtered = filter === 'all' ? events : events.filter(e => e.categories.includes(filter));

  return (
    <div>
      {/* Selector de ventana */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Ventana:</span>
        {[{ h: 24, l: '24 h' }, { h: 72, l: '3 días' }, { h: 168, l: '7 días' }].map(o => (
          <button key={o.h} onClick={() => setHours(o.h)} style={{
            fontSize: 12, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${hours === o.h ? 'var(--accent)' : 'var(--border-2)'}`,
            background: hours === o.h ? 'var(--amber-dim)' : 'var(--surface)',
            color: hours === o.h ? 'var(--accent)' : 'var(--ink-2)', fontWeight: hours === o.h ? 600 : 400 }}>
            {o.l}
          </button>
        ))}
        <button onClick={() => load(hours)} style={{ display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', border: '1px solid var(--border-2)', borderRadius: 6, background: 'var(--surface)',
          cursor: 'pointer', fontSize: 12, color: 'var(--ink-2)', marginLeft: 'auto' }}>
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} />}

      {!loading && !error && data && (
        <>
          {/* Stat strip por categoría */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 16 }}>
            {AUDIT_CATS.map(c => {
              const n = data.summary[c.id] || 0;
              return (
                <div key={c.id} onClick={() => setFilter(filter === c.id ? 'all' : c.id)}
                  style={{ border: `1px solid ${filter === c.id ? c.color : 'var(--border)'}`, borderRadius: 8, padding: '10px 12px',
                    cursor: 'pointer', background: filter === c.id ? 'var(--amber-dim)' : 'var(--surface)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <c.icon size={13} style={{ color: c.color }} />
                    <span style={{ fontSize: 10, color: 'var(--ink-2)', fontWeight: 600 }}>{c.label}</span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: n > 0 ? c.color : 'var(--ink-3)' }}>{n}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              {filtered.length} evento(s) notable(s) de {data.scanned} analizados · últimas {data.hours} h
            </span>
            {filter !== 'all' && (
              <button onClick={() => setFilter('all')} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5,
                border: '1px solid var(--border-2)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--ink-2)' }}>
                Quitar filtro: {AUDIT_CATS.find(c => c.id === filter)?.label}
              </button>
            )}
            {data.capped && <span style={{ fontSize: 11, color: 'var(--amber)' }}>(muestra parcial — acotá la ventana para ver todo)</span>}
          </div>

          {filtered.length === 0
            ? <div style={{ textAlign: 'center', padding: 48, color: 'var(--green)' }}>
                <ShieldCheck size={40} style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 600 }}>Sin eventos notables en esta ventana</div>
              </div>
            : filtered.map((e, i) => {
                const s = SEVERITY_STYLE[e.severity] || SEVERITY_STYLE.info;
                return (
                  <div key={e.id || i} style={{ border: `1px solid ${s.border}`, borderLeft: `3px solid ${s.color}`,
                    borderRadius: 8, padding: '10px 14px', marginBottom: 8, background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{e.eventName}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>{e.source}</span>
                      {e.categories.map(cat => {
                        const cc = AUDIT_CATS.find(x => x.id === cat);
                        return cc ? <span key={cat} style={{ fontSize: 10, fontWeight: 600, color: s.color,}}>{cc.label}</span> : null;
                      })}
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>{relTime(e.time)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      <span><strong style={{ color: 'var(--ink-2)' }}>Actor:</strong> {e.user} {e.userType === 'Root' && '(ROOT)'}</span>
                      {e.sourceIp && <span><strong style={{ color: 'var(--ink-2)' }}>IP:</strong> {e.sourceIp}</span>}
                      {e.region && <span><strong style={{ color: 'var(--ink-2)' }}>Región:</strong> {e.region}</span>}
                      {e.resource && <span><strong style={{ color: 'var(--ink-2)' }}>Recurso:</strong> {e.resource}</span>}
                      {e.mfaUsed && <span><strong style={{ color: 'var(--ink-2)' }}>MFA:</strong> {e.mfaUsed}</span>}
                      {e.errorCode && <span style={{ color: 'var(--red)' }}><strong>Error:</strong> {e.errorCode}</span>}
                    </div>
                  </div>
                );
              })
          }
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
            Fuente: CloudTrail (management events, historial de 90 días). Requiere el permiso <code>cloudtrail:LookupEvents</code>. Los eventos de datos (ej. acceso a objetos S3) no están incluidos.
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Logs (CloudWatch Logs Insights) ──────────────────────────────────────
function fmtBytes(b) {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

// Paleta categórica para colorear cada patrón de error de forma distinta
const LOG_PATTERN_COLORS = [
  '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#16a34a',
  '#0891b2', '#2563eb', '#7c3aed', '#db2777', 'var(--ink-2)',
];

function TabLogs({ accountId, token }) {
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsErr, setGroupsErr] = useState(null);
  const [logGroup, setLogGroup] = useState('');
  const [hours, setHours] = useState(24);
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!accountId || !token) return;
    setGroupsLoading(true); setGroupsErr(null); setGroups([]); setLogGroup(''); setResult(null); setState('idle');
    fetch(`${API}/api/aws/inventory/log-groups?accountId=${accountId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Error'); return d; })
      .then(d => setGroups(d.groups || []))
      .catch(e => setGroupsErr(e.message))
      .finally(() => setGroupsLoading(false));
  }, [accountId, token]);

  const analyze = () => {
    if (!logGroup) return;
    setState('loading'); setErr(null);
    fetch(`${API}/api/aws/inventory/logs-analysis?accountId=${accountId}&logGroup=${encodeURIComponent(logGroup)}&hours=${hours}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Error'); return d; })
      .then(d => { setResult(d); setState('done'); })
      .catch(e => { setErr(e.message); setState('error'); });
  };

  if (!accountId) return <EmptySelect />;

  const maxTrend = result ? Math.max(...result.trend.map(x => x.errores), 1) : 1;

  return (
    <div>
      <div style={{ background: 'var(--accent-dim)', border: '1px solid #bfdbfe', borderRadius: 8,
        padding: '12px 16px', marginBottom: 14, fontSize: 13, color: 'var(--accent)' }}>
        <strong>Análisis de errores por log group</strong> — tasa de errores, tendencia y patrones recurrentes
        vía CloudWatch Logs Insights.
        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>
          Insights escanea datos facturables (~US$0,005/GB). Elegí un log group y una ventana acotada.
        </div>
      </div>

      {/* Selector de log group + ventana */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={logGroup} onChange={e => setLogGroup(e.target.value)} disabled={groupsLoading}
          style={{ flex: 1, minWidth: 260, fontSize: 13, border: '1px solid var(--border-2)', borderRadius: 6, padding: '7px 10px', background: 'var(--surface)', color: 'var(--ink)' }}>
          <option value="">{groupsLoading ? 'Cargando log groups…' : '— Elegir log group —'}</option>
          {groups.map(g => (
            <option key={g.name} value={g.name}>{g.name} ({fmtBytes(g.storedBytes)})</option>
          ))}
        </select>
        <select value={hours} onChange={e => setHours(parseInt(e.target.value, 10))}
          style={{ fontSize: 13, border: '1px solid var(--border-2)', borderRadius: 6, padding: '7px 10px', background: 'var(--surface)', color: 'var(--ink)' }}>
          <option value={3}>3 h</option>
          <option value={24}>24 h</option>
          <option value={72}>3 días</option>
          <option value={168}>7 días</option>
        </select>
        <button onClick={analyze} disabled={!logGroup || state === 'loading'} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 6,
          background: logGroup ? 'var(--accent)' : 'var(--ink-3)', color: 'var(--surface)', fontWeight: 600, fontSize: 13,
          cursor: logGroup && state !== 'loading' ? 'pointer' : 'not-allowed' }}>
          <Activity size={15} /> Analizar
        </button>
      </div>

      {groupsErr && <ErrorMsg msg={groupsErr} />}
      {state === 'loading' && <Spinner />}
      {state === 'error' && <ErrorMsg msg={err} />}

      {state === 'done' && result && (
        <>
          {/* Headline */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600 }}>Eventos totales</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)' }}>{result.total.toLocaleString('es-AR')}</div>
            </div>
            <div style={{ border: '1px solid var(--amber-dim)', borderRadius: 8, padding: '12px 14px', background: 'var(--amber-dim)' }}>
              <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 600 }}>Errores</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)' }}>{result.totalErrors.toLocaleString('es-AR')}</div>
            </div>
            <div style={{ border: `1px solid ${result.errorRate > 5 ? 'var(--red-dim)' : 'var(--border)'}`, borderRadius: 8, padding: '12px 14px', background: result.errorRate > 5 ? 'var(--red-dim)' : 'var(--surface)' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600 }}>Tasa de error</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: result.errorRate > 5 ? 'var(--red)' : 'var(--ink)' }}>{result.errorRate}%</div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600 }}>Picos detectados</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: result.spikes.length ? 'var(--red)' : 'var(--green)' }}>{result.spikes.length}</div>
            </div>
          </div>

          {/* Trend — barras apiladas por patrón */}
          {(() => {
            const patterns = result.patterns || [];
            const patColor = (pk) => pk === '__otros'
              ? 'var(--ink-3)'
              : LOG_PATTERN_COLORS[(patterns.indexOf(pk) + LOG_PATTERN_COLORS.length) % LOG_PATTERN_COLORS.length];
            const hasOtros = result.trend.some(b => b.byPattern && b.byPattern.__otros);
            return (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10 }}>Errores por hora <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>· coloreado por patrón</span></div>
                {result.trend.length === 0
                  ? <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Sin errores en la ventana.</div>
                  : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 90 }}>
                        {result.trend.map((x, i) => {
                          const bp = x.byPattern || {};
                          const order = [...patterns, '__otros'].filter(pk => bp[pk] > 0);
                          const sum = order.reduce((a, pk) => a + bp[pk], 0) || 1;
                          const barH = Math.max(2, x.errores / maxTrend * 100);
                          const brk = order.map(pk => `${pk === '__otros' ? 'otros' : pk.slice(0, 40)}: ${bp[pk]}`).join('\n');
                          return (
                            <div key={i} title={`${new Date(x.t).toLocaleString('es-AR')} · ${x.errores} errores${brk ? '\n' + brk : ''}`}
                              style={{ flex: 1, minWidth: 3, height: `${barH}%`, display: 'flex', flexDirection: 'column',
                                borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                              {order.length === 0
                                ? <div style={{ flex: 1, background: 'var(--ink)' }} />
                                : order.map(pk => <div key={pk} style={{ height: `${bp[pk] / sum * 100}%`, background: patColor(pk) }} />)}
                            </div>
                          );
                        })}
                      </div>
                      {/* Leyenda */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px', marginTop: 12 }}>
                        {patterns.map((p, i) => (
                          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--ink-2)', maxWidth: 260 }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, background: patColor(p), flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{p}</span>
                          </span>
                        ))}
                        {hasOtros && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--ink-2)' }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--ink-3)', flexShrink: 0 }} />
                            Otros
                          </span>
                        )}
                      </div>
                    </>
                  )}
              </div>
            );
          })()}

          {/* Top errores */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10 }}>
              Top patrones de error {result.sampled && <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>(muestra de 1000)</span>}
            </div>
            {result.topErrors.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--green)' }}>Sin patrones de error detectados.</div>
              : result.topErrors.map((e, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: i < result.topErrors.length - 1 ? '1px solid var(--bg)' : 'none' }}>
                    <AlertOctagon size={14} style={{ color: 'var(--red)', flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', fontFamily: 'monospace', wordBreak: 'break-word' }}>{e.pattern}</div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)', flexShrink: 0 }}>{e.count}×</span>
                  </div>
                ))}
          </div>

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
            Log group: <code>{result.logGroup}</code> · últimas {result.hours} h. {result.priceNote}
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Optimización ─────────────────────────────────────────────────────────
const OPT_ICON = { EC2: Server, RDS: Database, Lambda: Zap, ECS: Package, S3: Archive,
  EBS: HardDrive, EIP: Globe, Snapshot: Camera };

function fmtUSD(n) {
  if (n == null) return null;
  return `US$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Tarjeta compacta para vista "tarjetas"
function OptCard({ r }) {
  const Icon = OPT_ICON[r.service] || Cloud;
  const saving = fmtUSD(r.monthlySaving);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--amber-dim)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} style={{ color: 'var(--accent)' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-2)' }}>{r.detail}</div>
        </div>
        {pill(r.service, '#f1f5f9', 'var(--ink-2)')}
      </div>
      <div style={{ fontSize: 12, color: 'var(--amber)' }}>{r.reason}</div>
      {r.monthlySaving != null && (
        <div style={{ fontSize: 13, fontWeight: 700, color: r.monthlySaving > 0 ? 'var(--green)' : 'var(--ink-3)' }}>
          {r.monthlySaving > 0 ? `${saving}/mes` : 'sin ahorro estimable'}
        </div>
      )}
    </div>
  );
}

// Renderiza una colección de recursos como lista (tabla) o tarjetas
function OptResources({ items, view }) {
  if (!items.length) return <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 2px' }}>Sin recursos.</div>;
  if (view === 'card') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {items.map((r, i) => <OptCard key={i} r={r} />)}
      </div>
    );
  }
  // list
  return (
    <Table cols={[
      { key: 'service', label: 'Servicio', render: v => pill(v, '#f1f5f9', 'var(--ink-2)') },
      { key: 'name',    label: 'Recurso' },
      { key: 'detail',  label: 'Detalle' },
      { key: 'reason',  label: 'Motivo', wrap: true },
      { key: 'monthlySaving', label: 'Ahorro/mes', render: v => v > 0
        ? <span style={{ color: 'var(--green)', fontWeight: 700 }}>{fmtUSD(v)}</span>
        : <span style={{ color: 'var(--ink-3)' }}>—</span> },
    ]} rows={items} />
  );
}

// Toggle lista / tarjetas
function ViewToggle({ view, setView }) {
  const btn = (id, Icon, label) => (
    <button onClick={() => setView(id)} style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
      border: '1px solid var(--border-2)', background: view === id ? 'var(--amber-dim)' : 'var(--surface)',
      color: view === id ? 'var(--accent)' : 'var(--ink-2)', fontWeight: view === id ? 600 : 400,
      borderRadius: id === 'card' ? '6px 0 0 6px' : '0 6px 6px 0',
      borderLeft: id === 'list' ? 'none' : '1px solid var(--border-2)',
    }}>
      <Icon size={14} /> {label}
    </button>
  );
  return (
    <div style={{ display: 'flex' }}>
      {btn('card', LayoutGrid, 'Tarjetas')}
      {btn('list', List, 'Lista')}
    </div>
  );
}

// Subgrupo colapsable liviano (para EBS/EIP/snapshots por origen)
function SubGroup({ title, icon: Icon, count, saving, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid var(--bg)', borderRadius: 6, marginBottom: 8, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        background: 'var(--bg-subtle)', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        {Icon && <Icon size={14} style={{ color: 'var(--ink-2)', flexShrink: 0 }} />}
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-2)', flex: 1 }}>{title}</span>
        {saving > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginRight: 8 }}>{fmtUSD(saving)}/mes</span>}
        <span style={{ fontSize: 12, color: 'var(--ink-3)', marginRight: 8 }}>{count}</span>
        {open ? <ChevronUp size={15} color="var(--ink-3)" /> : <ChevronDown size={15} color="var(--ink-3)" />}
      </button>
      {open && <div style={{ padding: 12 }}>{children}</div>}
    </div>
  );
}

const sumSaving = (arr) => +arr.reduce((s, r) => s + (r.monthlySaving || 0), 0).toFixed(2);

// Mini heatmap del perfil horario (24 columnas)
function HourHeatmap({ label, values }) {
  const max = Math.max(...values.filter(v => v != null), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 10, color: 'var(--ink-2)', width: 32, flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', gap: 1, flex: 1 }}>
        {values.map((v, h) => {
          const intensity = v == null ? 0 : v / max;
          const bg = v == null ? 'var(--bg)'
            : intensity > 0.5 ? 'var(--accent)'
            : intensity > 0.15 ? '#fdba74'
            : intensity > 0.02 ? 'var(--amber-dim)' : 'var(--border)';
          return <div key={h} title={`${h}:00 — ${v == null ? 's/d' : v + '%'}`}
            style={{ flex: 1, height: 14, background: bg, borderRadius: 1 }} />;
        })}
      </div>
    </div>
  );
}

function SchedulingSection({ accountId, token }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setState('loading'); setErr(null);
    try {
      const r = await fetch(`${API}/api/aws/inventory/scheduling?accountId=${accountId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error al analizar');
      setResult(d); setState('done');
    } catch (e) { setErr(e.message); setState('error'); }
  };

  return (
    <div>
      <div style={{ background: 'var(--accent-dim)', border: '1px solid #bfdbfe', borderRadius: 8,
        padding: '12px 16px', marginBottom: 12, fontSize: 13, color: 'var(--accent)' }}>
        <strong>Candidatos a apagado programado</strong> — analiza el patrón de uso por hora (14 días)
        para detectar EC2 que solo se usan en ciertas franjas y podrían apagarse el resto.
        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>
          Es un análisis más pesado (consulta CloudWatch hora por hora), por eso se ejecuta bajo demanda.
        </div>
      </div>

      {state === 'idle' && (
        <button onClick={run} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
          border: 'none', borderRadius: 8, background: 'var(--ink)', color: 'var(--surface)', fontWeight: 600,
          fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
          <Clock size={15} /> Analizar patrón horario (14 días)
        </button>
      )}

      {state === 'loading' && <Spinner />}
      {state === 'error' && <ErrorMsg msg={err} />}

      {state === 'done' && result && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              {result.analyzed} instancia(s) analizada(s) · {result.candidates.length} candidato(s)
            </div>
            <button onClick={run} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
              border: '1px solid var(--border-2)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', fontSize: 12, color: 'var(--ink-2)' }}>
              <RefreshCw size={13} /> Re-analizar
            </button>
          </div>

          {result.candidates.length === 0
            ? <div style={{ textAlign: 'center', padding: 32, color: 'var(--green)' }}>
                <CheckCircle size={32} style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 600 }}>Ningún EC2 muestra un patrón claro de apagado programado</div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4 }}>Todas las instancias analizadas tienen uso distribuido (24×7) o ya son idle.</div>
              </div>
            : result.candidates.map((r, i) => (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-dim)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Clock size={16} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{r.name} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>· {r.type}</span></div>
                      <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 2 }}>Ventana sugerida: <strong>{r.recommendedWindow}</strong></div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>Ocioso ~{r.idlePct}% de la semana ({r.activeHoursPerWeek} hs activas de 168)</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {r.monthlySaving != null && r.monthlySaving > 0 && (
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>{fmtUSD(r.monthlySaving)}/mes</div>
                      )}
                    </div>
                  </div>
                  <HourHeatmap label="L-V" values={r.profile.weekday} />
                  <HourHeatmap label="S-D" values={r.profile.weekend} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--ink-3)', marginTop: 2, paddingLeft: 40 }}>
                    <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                  </div>
                </div>
              ))
          }
          {result.candidates.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
              El perfil de CPU es una señal orientativa, no una certeza. Revisá cada caso (puede haber jobs nocturnos) antes de programar apagados. {result.priceNote}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TabOptimization({ data, loading, error, accountId, token }) {
  const [view, setView] = useState('card');

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} />;
  if (!data)   return <EmptySelect />;

  const { resources = [], waste = [], total, wasteTotal, totalMonthlySaving, priceNote } = data;

  // Desperdicio agrupado por tipo; snapshots sub-agrupados por volumen de origen
  const ebs   = waste.filter(r => r.service === 'EBS');
  const eip   = waste.filter(r => r.service === 'EIP');
  const snaps = waste.filter(r => r.service === 'Snapshot');
  const snapByOrigin = {};
  snaps.forEach(s => { const k = s.origin || 'vol-desconocido'; (snapByOrigin[k] = snapByOrigin[k] || []).push(s); });
  const snapOrigins = Object.entries(snapByOrigin).sort((a, b) => sumSaving(b[1]) - sumSaving(a[1]));

  return (
    <div>
      {/* Titular de ahorro + toggle de vista */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260, border: '1px solid var(--green-dim)', background: 'var(--green-dim)', borderRadius: 8,
          padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--green-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TrendingDown size={22} style={{ color: 'var(--green)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Ahorro mensual potencial estimado</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--green)' }}>{fmtUSD(totalMonthlySaving || 0)}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 2 }}>{priceNote}</div>
          </div>
        </div>
        <ViewToggle view={view} setView={setView} />
      </div>

      {/* Recursos potencialmente idle */}
      <Section title="Recursos potencialmente idle" icon={Activity} count={total} color="var(--amber)" defaultOpen>
        <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 10 }}>
          EC2 con CPU &lt;5% (7 días), RDS sin conexiones (7 días), Lambda sin invocaciones (30 días).
        </div>
        {total === 0
          ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--green)', fontWeight: 600 }}>No se detectaron recursos idle</div>
          : <OptResources items={resources} view={view} />}
      </Section>

      {/* Desperdicio puro */}
      <Section title="Desperdicio puro" icon={Trash2} count={wasteTotal} color="var(--red)" defaultOpen>
        <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>
          Recursos que existen y nadie usa: volúmenes EBS sin adjuntar, IPs elásticas sin asociar, snapshots viejos.
        </div>
        {wasteTotal === 0
          ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--green)', fontWeight: 600 }}>Sin recursos huérfanos detectados</div>
          : <>
              {ebs.length > 0 && (
                <SubGroup title="Volúmenes EBS sin adjuntar" icon={HardDrive} count={ebs.length} saving={sumSaving(ebs)} defaultOpen>
                  <OptResources items={ebs} view={view} />
                </SubGroup>
              )}
              {eip.length > 0 && (
                <SubGroup title="Elastic IPs sin asociar" icon={Globe} count={eip.length} saving={sumSaving(eip)} defaultOpen>
                  <OptResources items={eip} view={view} />
                </SubGroup>
              )}
              {snaps.length > 0 && (
                <SubGroup title="Snapshots viejos" icon={Camera} count={snaps.length} saving={sumSaving(snaps)} defaultOpen>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 8 }}>Agrupados por volumen de origen ({snapOrigins.length} orígenes).</div>
                  {snapOrigins.map(([origin, list]) => (
                    <SubGroup key={origin} title={`Origen: ${origin}`} icon={Camera} count={list.length} saving={sumSaving(list)}>
                      <OptResources items={list} view={view} />
                    </SubGroup>
                  ))}
                </SubGroup>
              )}
            </>}
      </Section>

      {/* Apagado programado (bajo demanda) */}
      <Section title="Candidatos a apagado programado" icon={Clock} count="análisis bajo demanda" color="var(--accent)" defaultOpen={false}>
        <SchedulingSection accountId={accountId} token={token} />
      </Section>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 64 }}>
      <RefreshCw size={24} color="var(--accent)" style={{ animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
function ErrorMsg({ msg }) {
  return (
    <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8,
      padding: '12px 16px', color: 'var(--red)', fontSize: 13 }}>{msg}</div>
  );
}
function EmptySelect() {
  return (
    <div style={{ textAlign: 'center', padding: 64, color: 'var(--ink-3)', fontSize: 13 }}>
      Seleccioná una cuenta AWS para ver los datos.
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AwsInventory() {
  const token = localStorage.getItem('token');
  const [tab, setTab] = useState('inventory');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [accountError, setAccountError] = useState(null);

  useEffect(() => {
    if (!token) return;
    setAccountError(null);
    fetch(`${API}/api/aws/inventory/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        const text = await r.text();
        let d;
        try { d = JSON.parse(text); } catch { throw new Error(`HTTP ${r.status}: ${text.slice(0,200)}`); }
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${d.error || text.slice(0,200)}`);
        return d;
      })
      .then(d => {
        const aws = Array.isArray(d) ? d : [];
        setAccounts(aws);
        if (aws.length === 1) setAccountId(aws[0].id);
      })
      .catch(e => setAccountError(e.message));
  }, [token]);

  const fetchTab = useCallback(async (t, accId) => {
    if (!accId) return;
    const endpointMap = {
      inventory:    '',
      security:     '/security',
      tags:         '/tags',
      optimization: '/idle',
    };
    const endpoint = `/api/aws/inventory${endpointMap[t]}?accountId=${accId}`;
    setLoading(l => ({ ...l, [t]: true }));
    setErrors(e => ({ ...e, [t]: null }));
    try {
      const r = await fetch(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error al cargar datos');
      setData(prev => ({ ...prev, [t]: d }));
    } catch (e) {
      setErrors(prev => ({ ...prev, [t]: e.message }));
    } finally {
      setLoading(l => ({ ...l, [t]: false }));
    }
  }, [token]);

  useEffect(() => {
    if (accountId && token) fetchTab(tab, accountId);
  }, [tab, accountId, token]);

  const tabStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: active ? 'var(--amber-dim)' : 'none',
    border: 'none', cursor: 'pointer', fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? 'var(--accent)' : 'var(--ink-2)',
    borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
    marginBottom: -2, whiteSpace: 'nowrap',
    transition: 'color 0.15s, background 0.15s',
  });

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--amber-dim)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Cloud size={20} style={{ color: 'var(--accent)' }} />
        </div>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>AWS Inventory</h1>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>Inventario, seguridad, tags y optimización de recursos AWS</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Account selector */}
          {accountError && (
            <span style={{ fontSize: 12, color: 'var(--red)', maxWidth: 300 }} title={accountError}>
              Error: {accountError.slice(0, 80)}
            </span>
          )}
          <select
            value={accountId}
            onChange={e => setAccountId(e.target.value)}
            style={{ fontSize: 13, border: `1px solid ${accountError ? 'var(--red)' : 'var(--border-2)'}`, borderRadius: 6,
              padding: '6px 10px', background: 'var(--surface)', color: 'var(--ink)', cursor: 'pointer' }}>
            <option value="">— Seleccionar cuenta —</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.environment})</option>
            ))}
          </select>
          {accountId && (
            <button onClick={() => fetchTab(tab, accountId)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              border: '1px solid var(--border-2)', borderRadius: 6, background: 'var(--surface)',
              cursor: 'pointer', fontSize: 13, color: 'var(--ink-2)' }}>
              <RefreshCw size={14} />
              Actualizar
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--border)', marginBottom: 20, display: 'flex', gap: 0 }}>
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          return (
            <button key={id} onClick={() => setTab(id)} style={tabStyle(active)}>
              <Icon size={14} style={{ color: active ? 'var(--accent)' : 'var(--ink-3)', flexShrink: 0 }} />
              {label}
              {id === 'security' && data.security?.critical > 0 && (
                <span style={{ background: 'var(--red)', color: 'var(--surface)', borderRadius: 10,
                  fontSize: 10, fontWeight: 700, padding: '0 5px', lineHeight: '16px' }}>
                  {data.security.critical}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div>
        {tab === 'inventory'    && <TabInventory    data={data.inventory}    loading={loading.inventory}    error={errors.inventory} />}
        {tab === 'security'     && <TabSecurity     data={data.security}     loading={loading.security}     error={errors.security} />}
        {tab === 'tags'         && <TabTags         data={data.tags}         loading={loading.tags}         error={errors.tags} />}
        {tab === 'optimization' && <TabOptimization data={data.optimization} loading={loading.optimization} error={errors.optimization} accountId={accountId} token={token} />}
        {tab === 'audit'        && <TabAudit        accountId={accountId} token={token} />}
        {tab === 'logs'         && <TabLogs         accountId={accountId} token={token} />}
      </div>
    </div>
  );
}
